import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { toast } from 'sonner'
import { ArrowLeft, ArrowRight, Loader2, Send, CalendarClock, Save, Pencil, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { PageHeader } from '@/components/shared'
import { emailCampaignService } from '@/services/emailCampaignService'
import { emailSegmentService } from '@/services/emailSegmentService'
import { emailTemplateService } from '@/services/emailTemplateService'
import { emailCustomFieldService } from '@/services/emailCustomFieldService'
import EmailPreview from '@/components/MarketingEmail/EmailPreview'
import EmailEditor from '@/components/MarketingEmail/EmailEditor'

const STEPS = ['Audience', 'Content', 'Review']
const CUSTOM_TEMPLATE_VALUE = 'custom'

const CampaignWizard = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditMode = !!id

  const [step, setStep] = useState(0)
  const [campaignId, setCampaignId] = useState(id || null)
  const [name, setName] = useState('')
  const [segmentId, setSegmentId] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [saving, setSaving] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [scheduleDate, setScheduleDate] = useState('')
  const [sendingNow, setSendingNow] = useState(false)
  const [status, setStatus] = useState('DRAFT')

  // Per-campaign copy of the template's content. Edits here only ever get saved onto the
  // campaign's own snapshot (backend `template_snapshot`) — the source template is untouched.
  const [subjectLine, setSubjectLine] = useState('')
  const [previewText, setPreviewText] = useState('')
  const [htmlBody, setHtmlBody] = useState('')
  const [previewData, setPreviewData] = useState(null)
  const contentInitializedRef = useRef(false)

  const [bodyEditOpen, setBodyEditOpen] = useState(false)

  const { data: segmentsData } = useQuery('email-segments', emailSegmentService.getAllSegments)
  const segments = segmentsData?.data?.segments || []

  const { data: templatesData } = useQuery('email-templates', emailTemplateService.getAllTemplates)
  const templates = templatesData?.data?.templates || []

  const { data: fieldsData } = useQuery('email-custom-fields', emailCustomFieldService.getAllFields)
  const customFieldDefinitions = fieldsData?.data?.fields || []

  const { data: campaignData, isLoading: loadingCampaign } = useQuery(
    ['email-campaign', id],
    () => emailCampaignService.getCampaignById(id),
    { enabled: isEditMode }
  )

  useEffect(() => {
    const campaign = campaignData?.data?.campaign
    if (campaign) {
      if (!['DRAFT', 'SCHEDULED'].includes(campaign.status)) {
        toast.error('Only draft or scheduled campaigns can be edited')
        navigate(`/marketing-email/campaigns/${id}`)
        return
      }
      setName(campaign.name)
      setSegmentId(String(campaign.segment_id))
      setTemplateId(campaign.template_id ? String(campaign.template_id) : CUSTOM_TEMPLATE_VALUE)
      setStatus(campaign.status)
      if (campaign.scheduled_at) setScheduleDate(campaign.scheduled_at.slice(0, 16))
    }
  }, [campaignData, id, navigate])

  // Seed the editable subject/preview/body once: prefer the campaign's own saved snapshot,
  // falling back to the live template's content if this campaign has never had one.
  useEffect(() => {
    const campaign = campaignData?.data?.campaign
    if (!campaign || contentInitializedRef.current) return
    const snapshot = campaign.template_snapshot
    if (snapshot) {
      setSubjectLine(snapshot.subject_line || '')
      setPreviewText(snapshot.preview_text || '')
      setHtmlBody(snapshot.html_body || '')
      contentInitializedRef.current = true
    } else if (templates.length > 0) {
      const t = templates.find((tpl) => String(tpl.id) === String(campaign.template_id))
      if (t) {
        setSubjectLine(t.subject_line || '')
        setPreviewText(t.preview_text || '')
        setHtmlBody(t.html_body || '')
        contentInitializedRef.current = true
      }
    }
  }, [campaignData, templates])

  // Debounced live preview of the (possibly unsaved) subject/preview/body, personalized with
  // sample data server-side the same way the template editor's preview works.
  useEffect(() => {
    if (!templateId) {
      setPreviewData(null)
      return
    }
    const handle = setTimeout(() => {
      emailTemplateService
        .previewContent({ subject_line: subjectLine, preview_text: previewText, html_body: htmlBody })
        .then((res) => setPreviewData(res.data))
        .catch(() => {})
    }, 400)
    return () => clearTimeout(handle)
  }, [templateId, subjectLine, previewText, htmlBody])

  const handleTemplateChange = (newId) => {
    setTemplateId(newId)
    if (newId === CUSTOM_TEMPLATE_VALUE) {
      // Custom content starts blank — it's never derived from (or saved as) a template.
      setSubjectLine('')
      setPreviewText('')
      setHtmlBody('')
      return
    }
    const t = templates.find((tpl) => String(tpl.id) === String(newId))
    if (t) {
      setSubjectLine(t.subject_line || '')
      setPreviewText(t.preview_text || '')
      setHtmlBody(t.html_body || '')
    }
  }

  const handleAddCustom = () => {
    handleTemplateChange(CUSTOM_TEMPLATE_VALUE)
    setBodyEditOpen(true)
  }

  const handleImageUpload = useCallback(async (file) => {
    if (!templateId || templateId === CUSTOM_TEMPLATE_VALUE) {
      toast.error('Image uploads need a template — custom content can\'t include images yet')
      return null
    }
    try {
      const res = await emailTemplateService.uploadImage(templateId, file)
      return res.data.url
    } catch (error) {
      toast.error(error.response?.data?.error || 'Image upload failed')
      return null
    }
  }, [templateId])

  const selectedSegment = segments.find((s) => String(s.id) === String(segmentId))
  const selectedTemplate = templates.find((t) => String(t.id) === String(templateId))

  const validateStep = (targetStep) => {
    if (targetStep >= 1 && (!name.trim() || !segmentId)) {
      toast.error('Please enter a campaign name and choose a segment')
      return false
    }
    if (targetStep >= 2 && !templateId) {
      toast.error('Please choose a template')
      return false
    }
    if (targetStep >= 2 && templateId === CUSTOM_TEMPLATE_VALUE && (!subjectLine.trim() || !htmlBody.trim())) {
      toast.error('Write a subject line and email body first')
      return false
    }
    return true
  }

  const saveCampaign = async (scheduledAt) => {
    const payload = {
      name,
      segment_id: Number(segmentId),
      template_id: templateId === CUSTOM_TEMPLATE_VALUE ? null : Number(templateId),
      scheduled_at: scheduledAt || null,
      subject_line: subjectLine,
      preview_text: previewText,
      html_body: htmlBody
    }
    if (campaignId) {
      const res = await emailCampaignService.updateCampaign(campaignId, payload)
      return res.data.campaign
    }
    const res = await emailCampaignService.createCampaign(payload)
    setCampaignId(res.data.campaign.id)
    return res.data.campaign
  }

  const handleSaveDraft = async () => {
    if (!validateStep(2)) return
    setSaving(true)
    try {
      await saveCampaign(null)
      toast.success('Campaign saved as draft')
      navigate('/marketing-email/campaigns')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save campaign')
    } finally {
      setSaving(false)
    }
  }

  const handleSchedule = async () => {
    if (!scheduleDate) {
      toast.error('Choose a date and time')
      return
    }
    if (new Date(scheduleDate) <= new Date()) {
      toast.error('Scheduled time must be in the future')
      return
    }
    setSaving(true)
    try {
      await saveCampaign(new Date(scheduleDate).toISOString())
      toast.success('Campaign scheduled')
      setScheduleOpen(false)
      navigate('/marketing-email/campaigns')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to schedule campaign')
    } finally {
      setSaving(false)
    }
  }

  const handleSendNow = async () => {
    if (!validateStep(2)) return
    setSendingNow(true)
    try {
      const campaign = await saveCampaign(null)
      await emailCampaignService.sendNow(campaign.id)
      toast.success('Campaign sent')
      navigate(`/marketing-email/campaigns/${campaign.id}`)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send campaign')
    } finally {
      setSendingNow(false)
    }
  }

  if (isEditMode && loadingCampaign) {
    return <div className="text-center py-12 text-muted-foreground">Loading...</div>
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditMode ? 'Edit Campaign' : 'New Campaign'}
        description="Send a personalized email to a segment of your contacts"
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/marketing-email/campaigns')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        }
      />

      <div className="flex items-center gap-2 text-sm">
        {STEPS.map((label, idx) => (
          <React.Fragment key={label}>
            {idx > 0 && <div className="h-px w-8 bg-border" />}
            <button
              className={`flex items-center gap-1.5 ${idx === step ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
              onClick={() => (idx < step || validateStep(idx)) && setStep(idx)}
            >
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${idx <= step ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                {idx + 1}
              </span>
              {label}
            </button>
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <Card>
          <CardContent className="p-6 space-y-4 max-w-xl">
            <div className="space-y-1.5">
              <Label>Campaign Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. July Product Update" />
            </div>
            <div className="space-y-1.5">
              <Label>Audience Segment</Label>
              <Select value={segmentId} onValueChange={setSegmentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a segment" />
                </SelectTrigger>
                <SelectContent>
                  {segments.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedSegment && (
              <div className="rounded-lg border p-3 text-sm">
                <span className="text-muted-foreground">Estimated recipients: </span>
                <span className="font-medium">{selectedSegment.last_evaluated_count ?? '—'}</span>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={() => validateStep(1) && setStep(1)}>
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              <Label>Email Template</Label>
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <Select value={templateId === CUSTOM_TEMPLATE_VALUE ? '' : templateId} onValueChange={handleTemplateChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={handleAddCustom}>
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Add Custom
                </Button>
              </div>
              {templates.length === 0 && (
                <p className="text-sm text-muted-foreground">No templates yet — use "Add Custom" to write one for this campaign.</p>
              )}

              <div className="space-y-1.5">
                <Label>Subject Line</Label>
                <Input value={subjectLine} onChange={(e) => setSubjectLine(e.target.value)} placeholder="e.g. Your {{month}} update is here" />
              </div>
              <div className="space-y-1.5">
                <Label>Preview Text</Label>
                <Input value={previewText} onChange={(e) => setPreviewText(e.target.value)} placeholder="Shown next to the subject line in most inboxes" />
              </div>
              {templateId && (
                <p className="text-xs text-muted-foreground">
                  {templateId === CUSTOM_TEMPLATE_VALUE
                    ? 'This content is written just for this campaign — it won\'t be saved as a reusable template.'
                    : 'These only apply to this campaign — the original template is not changed.'}
                </p>
              )}

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
                <Button onClick={() => validateStep(2) && setStep(2)}>
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          <div>
            {templateId ? (
              <EmailPreview
                subject={previewData?.subject}
                previewText={previewData?.previewText}
                html={previewData?.html}
                actions={
                  <Button variant="outline" size="sm" onClick={() => setBodyEditOpen(true)}>
                    <Pencil className="mr-2 h-3.5 w-3.5" />
                    Edit
                  </Button>
                }
              />
            ) : (
              <div className="rounded-lg border bg-muted/30 p-12 text-center text-sm text-muted-foreground">
                Choose a template to preview it here
              </div>
            )}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-medium">Campaign Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Audience</span><span className="font-medium">{selectedSegment?.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Est. Recipients</span><span className="font-medium">{selectedSegment?.last_evaluated_count ?? '—'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Template</span><span className="font-medium">{templateId === CUSTOM_TEMPLATE_VALUE ? 'Custom (no template)' : selectedTemplate?.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Subject</span><span className="font-medium">{previewData?.subject}</span></div>
                {status !== 'DRAFT' && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant="secondary">{status}</Badge></div>
                )}
              </div>

              <div className="flex flex-col gap-2 pt-4">
                <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Save className="mr-2 h-4 w-4" />
                  Save as Draft
                </Button>
                <Button variant="outline" onClick={() => setScheduleOpen(true)}>
                  <CalendarClock className="mr-2 h-4 w-4" />
                  Schedule for Later
                </Button>
                <Button onClick={handleSendNow} disabled={sendingNow}>
                  {sendingNow && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Send className="mr-2 h-4 w-4" />
                  Send Now
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>Back</Button>
            </CardContent>
          </Card>
          <EmailPreview
            subject={previewData?.subject}
            previewText={previewData?.previewText}
            html={previewData?.html}
          />
        </div>
      )}

      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Schedule Campaign</DialogTitle>
            <DialogDescription>Choose when this campaign should be sent.</DialogDescription>
          </DialogHeader>
          <Input
            type="datetime-local"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>Cancel</Button>
            <Button onClick={handleSchedule} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bodyEditOpen} onOpenChange={setBodyEditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Email Body</DialogTitle>
            <DialogDescription>
              This only applies to this campaign — the original template is not changed.
            </DialogDescription>
          </DialogHeader>
          <EmailEditor
            content={htmlBody}
            onChange={setHtmlBody}
            onImageUpload={handleImageUpload}
            customFieldDefinitions={customFieldDefinitions}
            placeholder="Write your email content here..."
          />
          <DialogFooter>
            <Button onClick={() => setBodyEditOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default CampaignWizard
